import React, { useContext, useEffect, useRef, useState } from "react"
import { Spinner } from "./Spinner.jsx"
import { GlobalStateContext } from "./providers/GlobalStateProvider.jsx"
import { Button } from "@shopify/polaris"
import { CreateFeedbackPostModal } from "./CreateFeedbackPostModal.jsx"
import { LoadingOverlay } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useFirestoreDataUpdate } from "../hooks/useFirestoreDataUpdate.js"
import { useMediaQuery } from "../hooks/useMediaQuery.js"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"

const Post = ({ post, isVoted, canVote, onVoteSuccess, onUnvoteSuccess }) => {
	const { state } = useContext(GlobalStateContext)
	const [loadingVote, setLoadingVote] = useState(false)
	const authenticatedFetch = useAuthenticatedFetch()

	const votePost = async (addOrRemove) => {
		if (!canVote) return
		if (!state.userId) return
		if (loadingVote) return

		if (addOrRemove === "add") {
			onVoteSuccess(post.id)
		} else {
			onUnvoteSuccess(post.id)
		}

		setLoadingVote(true)

		const response = await authenticatedFetch("/api/feedback/votePost", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				postId: post.id,
				vote: addOrRemove,
			}),
		}).catch((error) => {
			console.error("Error:", error)
			return null
		})

		const data = await response?.json().catch(() => null)
		if (data?.success === false) {
			if (addOrRemove === "add") {
				onUnvoteSuccess(post.id)
			} else {
				onVoteSuccess(post.id)
			}
		}

		setLoadingVote(false)
	}

	return (
		<div
			key={post.id}
			style={{
				display: "flex",
				gap: "0.1rem",
				background: "#f5f5f5",
				borderRadius: "0.5rem",
				padding: "0.5rem",
				paddingLeft: "1rem",
				marginBottom: "0.5rem",
			}}
		>
			<div style={{ flex: "1" }}>
				<h3
					style={{
						fontWeight: "500",
						fontSize: "0.9rem",
						color: "#333",
					}}
				>
					{post.title}
				</h3>
				<p
					style={{
						fontWeight: "400",
						fontSize: "0.8rem",
						color: "#333",
					}}
				>
					{post.details}
				</p>
			</div>

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					marginLeft: "auto",
					width: "3rem",
					height: "100%",
					gap: "0.2rem",
				}}
			>
				{canVote ? (
					isVoted ? (
						<button
							type="button"
							aria-label="Remove vote"
							onClick={() => votePost("remove")}
							style={{
								height: "1.2rem",
								width: "1.2rem",
								color: "#5371ff",
								cursor: "pointer",
								background: "transparent",
								border: "none",
								padding: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<svg
								style={{
									height: "1.2rem",
									width: "1.2rem",
									color: "currentColor",
								}}
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									fill="currentColor"
									d="M4 14h4v7a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-7h4a1.001 1.001 0 0 0 .781-1.625l-8-10c-.381-.475-1.181-.475-1.562 0l-8 10A1.001 1.001 0 0 0 4 14"
								></path>
							</svg>
						</button>
					) : (
						<button
							type="button"
							aria-label="Add vote"
							onClick={() => votePost("add")}
							style={{
								height: "1.2rem",
								width: "1.2rem",
								color: "#333",
								cursor: "pointer",
								background: "transparent",
								border: "none",
								padding: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<svg
								style={{
									height: "1.2rem",
									width: "1.2rem",
									color: "currentColor",
								}}
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									fill="currentColor"
									d="M12.781 2.375c-.381-.475-1.181-.475-1.562 0l-8 10A1.001 1.001 0 0 0 4 14h4v7a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-7h4a1.001 1.001 0 0 0 .781-1.625zM15 12h-1v8h-4v-8H6.081L12 4.601L17.919 12z"
								></path>
							</svg>
						</button>
					)
				) : (
					<div style={{ height: "1.2rem", width: "1.2rem" }} />
				)}
				<span
					style={{
						fontSize: "0.75rem",
						fontWeight: "600",
						color: isVoted ? "#5371ff" : "#333",
					}}
				>
					{post.score.toLocaleString()}
				</span>
			</div>
		</div>
	)
}

export const Feedback = () => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const updateFirestoreUserData = useFirestoreDataUpdate()
	const isMobile = useMediaQuery("(max-width: 460px)")
	const authenticatedFetch = useAuthenticatedFetch()

	const [hidden, setHidden] = useState(true)
	const [tab, setTab] = useState("active") // active, completed
	const [votedPostIds, setVotedPostIds] = useState([])
	const [showCreatePostModal, setShowCreatePostModal] = useState(false)

	const [posts, setPosts] = useState([])
	const [loadingPosts, setLoadingPosts] = useState(false)

	const [hasMore, setHasMore] = useState(true)
	const hasMoreRef = useRef(true)
	const [skipPosts, setSkipPosts] = useState(0)
	const skipPostsRef = useRef(0)

	const scrollRef = useRef(null)

	const changeHidden = (change) => {
		if (change === true) {
			updateFirestoreUserData({ hasClosedFeedback: true })
			dispatch({ hasClosedFeedback: true })
		}
		setHidden(change)
	}

	const changeTab = (newTab) => {
		if (tab === newTab) return
		if (loadingPosts) return
		setTab(newTab)

		scrollRef.current.scrollTo(0, 0)

		setSkipPosts(0)
		skipPostsRef.current = 0
	}

	const getPosts = async () => {
		if (!state.userId) {
			setPosts([])
			setVotedPostIds([])
			setHasMore(false)
			hasMoreRef.current = false
			return
		}

		const status = tab === "active" ? "active" : "completed"

		setLoadingPosts(true)
		const response = await authenticatedFetch(
			`/api/feedback/getPostsAndVotes?status=${status}&skip=${skipPostsRef.current}`,
		).catch((error) => {
			console.error("Error:", error)
			return null
		})

		const data = await response?.json().catch(() => null)
		if (data) {
			if (skipPostsRef.current !== 0) {
				setPosts((prev) => [...prev, ...(data?.posts || [])])
			} else {
				setPosts(data?.posts || [])
			}

			setVotedPostIds(data?.votedPostIds || [])

			setHasMore(data?.hasMore || false)
			hasMoreRef.current = data?.hasMore || false

			if (data?.posts?.length) {
				setSkipPosts((prev) => prev + data.posts.length)
				skipPostsRef.current = skipPostsRef.current + data.posts.length
			}
		}
		setLoadingPosts(false)
	}

	const onVoteSuccess = (postId) => {
		setVotedPostIds((prev) => {
			if (prev.includes(postId)) {
				return prev.filter((id) => id !== postId)
			} else {
				return [...prev, postId]
			}
		})

		setPosts((prev) =>
			prev.map((post) => {
				if (post.id === postId) {
					return { ...post, score: post.score + 1 }
				}
				return post
			}),
		)
	}

	const onUnvoteSuccess = (postId) => {
		setVotedPostIds((prev) => prev.filter((id) => id !== postId))

		setPosts((prev) =>
			prev.map((post) => {
				if (post.id === postId) {
					return { ...post, score: post.score - 1 }
				}
				return post
			}),
		)
	}

	useEffect(() => {
		getPosts()
	}, [tab, state.userId])

	const handleScroll = () => {
		if (!scrollRef.current) return

		const { scrollTop, scrollHeight, clientHeight } = scrollRef.current

		const hasScrolledToBottom =
			Math.ceil(scrollTop) + clientHeight >= scrollHeight

		if (
			scrollTop > 0 &&
			hasScrolledToBottom &&
			!loadingPosts &&
			hasMoreRef.current
		) {
			getPosts()
		}
	}

	useEffect(() => {
		const div = scrollRef.current
		if (div) {
			div.addEventListener("scroll", handleScroll)
			return () => div.removeEventListener("scroll", handleScroll)
		}
	}, [])

	useEffect(() => {
		if (!state.loadingUserData && !state.hasClosedFeedback && !isMobile) {
			changeHidden(false)
		}
	}, [state.loadingUserData, state.hasClosedFeedback])

	return (
		<div
			id="feedback"
			className={`border ${hidden ? "hidden" : ""}`}
			style={{
				position: "relative",
				width: "25rem",
				background: "white",
				borderRadius: "1rem",
				padding: "0.5rem",
				zIndex: "500",
				pointerEvents: "all",
			}}
		>
			<div style={{ padding: "0rem 0.5rem", marginBottom: "0.5rem" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.3rem",
						}}
					>
						<svg
							style={{
								width: "1.1rem",
								height: "1.1rem",
								color: "#333",
							}}
							viewBox="0 0 32 32"
						>
							<path
								fill="currentColor"
								d="M19.95 15.89c.18.08.37.11.55.11c.41 0 .81-.17 1.1-.48L23.94 13H27c1.65 0 3-1.35 3-3V5c0-1.65-1.35-3-3-3h-8c-1.65 0-3 1.35-3 3v4.99c0 1.65 1.35 3 3 3v1.5c0 .63.37 1.17.95 1.4M10.5 18c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5m0 11.99c-2.9 0-5.12-.8-6.62-2.368c-1.931-2.015-1.886-4.585-1.881-4.838v-.017C2 21.26 3.26 20 4.82 20h11.36c1.55 0 2.82 1.259 2.82 2.817v.01c.004.182.06 2.77-1.88 4.805C15.62 29.201 13.4 30 10.5 30z"
							></path>
						</svg>
						<h2
							style={{
								fontWeight: "600",
								fontSize: "1.1rem",
								color: "#333",
							}}
						>
							{t("components.feedback.feedback")}
						</h2>
					</div>
					<div
						onClick={() => changeTab("active")}
						style={{
							background: tab === "active" ? "#c2e1ff" : "#eee",
							color: tab === "active" ? "#154cac" : "#333",
							fontWeight: "500",
							fontSize: "0.8rem",
							padding: "0.1rem 0.5rem",
							borderRadius: "0.3rem",
							cursor: "pointer",
							marginLeft: "auto",
						}}
					>
						{t("components.feedback.active")}
					</div>
					<div
						onClick={() => changeTab("completed")}
						style={{
							background:
								tab === "completed" ? "#c2e1ff" : "#eee",
							color: tab === "completed" ? "#154cac" : "#333",
							fontWeight: "500",
							fontSize: "0.8rem",
							padding: "0.1rem 0.5rem",
							borderRadius: "0.3rem",
							cursor: "pointer",
						}}
					>
						{t("components.feedback.completed")}
					</div>
				</div>
			</div>

			<div
				style={{
					height: "20rem",
					paddingRight: "0.5rem",
					overflowY: "scroll",
				}}
				ref={scrollRef}
			>
				<LoadingOverlay
					visible={loadingPosts && posts.length}
					overlayBlur={2}
					style={{ borderRadius: "1rem" }}
					loader={<Spinner />}
				/>
				{loadingPosts && posts.length === 0 ? (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Spinner />
					</div>
				) : posts.length ? (
					posts.map((post) => (
						<Post
							key={post.id}
							post={post}
							isVoted={votedPostIds.includes(post.id)}
							canVote={tab === "active"}
							onVoteSuccess={onVoteSuccess}
							onUnvoteSuccess={onUnvoteSuccess}
						/>
					))
				) : (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<h3
							style={{
								fontWeight: "500",
								fontSize: "0.9rem",
								color: "#777",
							}}
						>
							{t("components.feedback.no_posts_found")}
						</h3>
					</div>
				)}
			</div>

			<div
				style={{
					marginTop: "0.5rem",
					display: "flex",
					justifyContent: "center",
				}}
			>
				<Button onClick={() => setShowCreatePostModal(true)}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.3rem",
						}}
					>
						<svg
							style={{ height: "1.2rem", width: "1.2rem" }}
							viewBox="0 0 24 24"
						>
							<path
								fill="none"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M3 12h1m8-9v1m8 8h1M5.6 5.6l.7.7m12.1-.7l-.7.7M9 16a5 5 0 1 1 6 0a3.5 3.5 0 0 0-1 3a2 2 0 0 1-4 0a3.5 3.5 0 0 0-1-3m.7 1h4.6"
							></path>
						</svg>
						<span style={{ fontSize: "0.9rem" }}>
							{t("components.feedback.propose_idea")}
						</span>
					</div>
				</Button>
				<CreateFeedbackPostModal
					show={showCreatePostModal}
					setShow={setShowCreatePostModal}
				/>
			</div>

			<div
				onClick={() => changeHidden(!hidden)}
				className="border"
				style={{
					top: isMobile && !hidden ? "-35px" : "44%",
					left:
						isMobile && !hidden
							? "48%"
							: hidden
								? "-4.5rem"
								: "-2rem",
					position: "absolute",
					transform: "rotate(270deg)",
					display: "flex",
					alignItems: "center",
					gap: "0.4rem",
					background: "white",
					padding: hidden ? "0.2rem 0.5rem" : "0.2rem",
					borderRadius: "0.5rem",
					cursor: "pointer",
				}}
			>
				{hidden ? (
					<svg
						style={{ width: "1rem", height: "1rem", color: "#333" }}
						viewBox="0 0 32 32"
					>
						<path
							fill="currentColor"
							d="M19.95 15.89c.18.08.37.11.55.11c.41 0 .81-.17 1.1-.48L23.94 13H27c1.65 0 3-1.35 3-3V5c0-1.65-1.35-3-3-3h-8c-1.65 0-3 1.35-3 3v4.99c0 1.65 1.35 3 3 3v1.5c0 .63.37 1.17.95 1.4M10.5 18c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5m0 11.99c-2.9 0-5.12-.8-6.62-2.368c-1.931-2.015-1.886-4.585-1.881-4.838v-.017C2 21.26 3.26 20 4.82 20h11.36c1.55 0 2.82 1.259 2.82 2.817v.01c.004.182.06 2.77-1.88 4.805C15.62 29.201 13.4 30 10.5 30z"
						></path>
					</svg>
				) : (
					<svg
						style={{
							width: "0.9rem",
							height: "0.9rem",
							color: "#333",
						}}
						viewBox="0 0 16 16"
					>
						<path
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="1.5"
							d="M3.75 5.75L8 10.25l4.25-4.5"
						></path>
					</svg>
				)}
				{hidden ? (
					<span
						style={{
							fontWeight: "500",
							fontSize: "0.85rem",
							color: "#333",
						}}
					>
						{t("components.feedback.feedback")}
					</span>
				) : (
					""
				)}
			</div>

			<style>{`
				#feedback {
					transition: all 0.5s;
					right: 1rem;
				}
				#feedback.hidden {
					right: -25rem;
				}
			}`}</style>
		</div>
	)
}
